import { useEffect, useState, useSyncExternalStore } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * ErrorToast — a non-blocking bottom-right toast for unexpected failures.
 *
 * The Watch uses an antiquarian aesthetic (brass, parchment, walnut) so this
 * intentionally reads like a small brass plate pushed onto the user's screen
 * rather than a generic system "Something went wrong" dialog. Two states:
 *
 *   - showError(msg)   — unexpected / unhandled. Auto-dismiss after 8s.
 *   - showWarning(msg) — soft failure (e.g. optional feature unavailable).
 *                        Same look; auto-dismiss after 6s. Role="status" instead
 *                        of "alert" so it doesn't interrupt screen readers.
 *
 * The render is mount-once: <ErrorToast /> in App.tsx, OUTSIDE ErrorBoundary,
 * so even a render-phase error in some subtree still gets shown to the user.
 *
 * Communication uses a module-level EventTarget — no Context, no Zustand,
 * no new dep. Anywhere in the app can call showError(...) without being a
 * child of the toast. The component reads the queue via useSyncExternalStore
 * so React 18 stays happy with concurrent mode.
 *
 * PII-safe: callers must pass a short, generic message (e.g. "Couldn't load
 * chart", "Refresh failed"). Do NOT include chart payloads, names, IDs, or
 * raw exception text. Full stack goes to Sentry separately.
 */

type Severity = "error" | "warning";

interface ToastEntry {
  id: number;
  message: string;
  severity: Severity;
}

const QUEUE: ToastEntry[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

// Snapshot used by getSnapshot. We swap the reference on every push/
// dismiss so useSyncExternalStore sees a change and schedules a
// re-render. The QUEUE array itself is mutated in place by the
// push/dismiss functions (they use push/splice for simplicity) — but
// React's Object.is check requires a NEW reference, not a mutation,
// so we expose a fresh array on every notify().
let snapshotCache: ToastEntry[] = [];

function takeSnapshot(): ToastEntry[] {
  // Always return the cache reference; refresh it only when notify runs.
  return snapshotCache;
}

function refreshSnapshot(): void {
  // Fresh array — different identity from the live QUEUE so React's
  // Object.is check inside useSyncExternalStore trips.
  snapshotCache = QUEUE.slice();
}

// Auto-dismiss timers keyed by entry id so a showError() followed by a
// showWarning() doesn't accidentally dismiss the first when the second fires.
const timers = new Map<number, ReturnType<typeof setTimeout>>();

const AUTO_DISMISS_MS: Record<Severity, number> = {
  error: 8000,
  warning: 6000,
};

function notify() {
  refreshSnapshot();
  listeners.forEach((l) => l());
}

function showError(message: string) {
  push(message, "error");
}

function showWarning(message: string) {
  push(message, "warning");
}

type Pusher = ((message: string, severity: Severity) => void) & { calledOnce?: boolean };

function push(message: string, severity: Severity) {
  const id = nextId++;
  const entry: ToastEntry = { id, message, severity };
  QUEUE.push(entry);
  notify();

  const timer = setTimeout(() => {
    dismiss(id);
  }, AUTO_DISMISS_MS[severity]);
  timers.set(id, timer);
}

function dismiss(id: number) {
  const timer = timers.get(id);
  if (timer) {
    clearTimeout(timer);
    timers.delete(id);
  }
  const idx = QUEUE.findIndex((e) => e.id === id);
  if (idx >= 0) {
    QUEUE.splice(idx, 1);
    notify();
  }
}

type Listener = () => void;
type ListenerWithCache = Listener & { __cleanup?: () => void };

function subscribe(cb: Listener): () => void {
  listeners.add(cb);
  // React's useSyncExternalStore expects the unsubscribe function to
  // be the same reference across subscribe() calls for the same cb —
  // we cache it on the callback object so React's Object.is check
  // passes and the subscription stays stable across re-renders.
  const tagged = cb as ListenerWithCache;
  return tagged.__cleanup ?? (tagged.__cleanup = () => listeners.delete(cb));
}

// useSyncExternalStore wants a stable snapshot reference between
// calls when the store hasn't changed. We return the cache, which
// gets a fresh reference (via refreshSnapshot) on every notify() —
// so React's Object.is check trips and schedules a re-render.
function getSnapshot(): ToastEntry[] {
  return takeSnapshot();
}

// SSR-safe: on the server `window` is undefined; emit/show become no-ops.
function getServerSnapshot(): ToastEntry[] {
  return [];
}

export { showError, showWarning };

export default function ErrorToast() {
  const { t } = useTranslation();
  const toasts = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  // Cleanup all pending timers on unmount so dev HMR doesn't leak them.
  useEffect(() => {
    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);

  // Test-only trigger: when ?toastTest=1 is in the URL, fire a toast
  // once on mount. Used by tests/error-toast.smoke.spec.js to assert
  // the toast component contract end-to-end without depending on
  // browser-specific window.error / unhandledrejection semantics
  // (which vary between Playwright's bundled Chromium and real browsers).
  // Only fires when the query param is present — production users
  // never see this code path. The auto-dismiss timer still fires so
  // the test cleanup passes naturally.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const trigger = params.get("toastTest");
    const pusher = push as Pusher;
    if ((trigger === "1" || trigger === "2") && !pusher.calledOnce) {
      // Idempotency guard — React.StrictMode double-mounts in dev,
      // which would push twice. Use a module-level flag so the
      // second mount is a no-op.
      pusher.calledOnce = true;
      const severity: Severity = trigger === "2" ? "warning" : "error";
      push("Toast test message — Playwright smoke", severity);
    }
    // intentionally empty deps — fires once per mount, but the
    // flag above keeps StrictMode's double-mount from duplicating
    // the toast.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      className="fixed bottom-4 end-4 z-50 flex flex-col gap-2 pointer-events-none"
    >
      <AnimatePresence initial={false}>
        {toasts.map((entry) => (
          <motion.div
            key={entry.id}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, x: 20, scale: 0.95 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            role={entry.severity === "error" ? "alert" : "status"}
            className={[
              "pointer-events-auto",
              "max-w-sm min-w-[260px]",
              "flex items-start gap-3",
              "px-4 py-3",
              "border shadow-2xl",
              entry.severity === "error"
                ? "bg-walnut-900/95 border-vermillion-700/60 text-parchment-100"
                : "bg-walnut-900/95 border-brass-500/50 text-parchment-100",
            ].join(" ")}
          >
            <AlertTriangle
              className={[
                "w-4 h-4 mt-0.5 flex-shrink-0",
                entry.severity === "error" ? "text-vermillion-300" : "text-brass-300",
              ].join(" ")}
              aria-hidden="true"
            />
            <p className="font-body text-[13px] leading-relaxed flex-1">
              {entry.message}
            </p>
            <button
              type="button"
              onClick={() => dismiss(entry.id)}
              aria-label={t("ui.web.long_tail.errors.dismiss_notification")}
              className="text-parchment-500 hover:text-parchment-200 transition-colors flex-shrink-0"
            >
              <X className="w-4 h-4" aria-hidden="true" />
            </button>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

// ── Tiny self-test for the queue machinery (used by the smoke test) ────────
// Exported only so unit tests / Playwright can verify the showError path
// without poking the EventTarget internals. Not part of the component API.
export const __testing = {
  _dismiss: dismiss,
  _queue: QUEUE,
  _push: push,
};
